import React, { useEffect, useMemo, useState } from "react";
import "../news/news.css";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import NewsRadarDashboard from "../../components/NewsRadarDashboard";

const escapeRegExp = (value) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const highlightText = (text = "", term = "") => {
  const trimmedTerm = term.trim();

  if (!trimmedTerm) return text;

  const regex = new RegExp(`(${escapeRegExp(trimmedTerm)})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, index) =>
    part.toLowerCase() === trimmedTerm.toLowerCase() ? (
      <mark key={`${part}-${index}`} className="news-highlight">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

const getAssetArticleCount = (asset) =>
  Number(asset?.total_news_count ?? asset?.news?.length ?? 0);

const getClusterSources = (cluster) => {
  if (Array.isArray(cluster?.domains)) return cluster.domains.length;
  if (Array.isArray(cluster?.articles)) return cluster.articles.length;
  return 0;
};

const escapeMarkdown = (value = "") =>
  String(value)
    .replace(/\\/g, "\\\\")
    .replace(/\|/g, "\\|")
    .replace(/\r?\n/g, " ")
    .trim();

const createMarkdownFileName = (value = "news-intelligence") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80) || "news-intelligence";

const NewsLoadingState = () => (
  <main className="news-page news-loading-page">
    <div className="news-loading-state" role="status" aria-live="polite">
      <span>Loading news intelligence</span>
      <div aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
    </div>
  </main>
);

const EmptyState = ({ title, message }) => (
  <div className="news-empty-state">
    <span className="news-section-kicker">No reporting available</span>
    <h2>{title}</h2>
    <p>{message}</p>
  </div>
);

export const News = () => {
  const navigate = useNavigate();

  const [userToken, setUserToken] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsData, setNewsData] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(null);
  const [selectedGroupName, setSelectedGroupName] = useState(null);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [exitingNews, setExitingNews] = useState(new Set());
  const [dismissedNews, setDismissedNews] = useState(new Set());
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: "",
    end: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------- AUTH ---------- */
  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        setUserToken(session.tokens.accessToken.toString());
      } catch {
        navigate("/login");
      } finally {
        setAuthChecked(true);
      }
    })();
  }, [navigate]);

  /* ---------- FETCH ---------- */
  useEffect(() => {
    const fetchMyNews = async () => {
      if (!userToken) return;

      setNewsLoading(true);

      try {
        const response = await fetch(
          "https://api.weathervaneintel.com/api/api/fetchMyNews",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify([{ userToken }]),
          }
        );

        if (response.status === 403) {
          navigate("/login");
          return;
        }

        if (!response.ok) {
          console.warn("API Error:", response.status, response.statusText);
          return;
        }

        const data = await response.json();
        setNewsData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Network or fetch error:", err);
      } finally {
        setNewsLoading(false);
      }
    };

    fetchMyNews();
  }, [userToken, navigate]);

  useEffect(() => {
    setDismissedNews(new Set());
    setExitingNews(new Set());
  }, [selectedGroupName, selectedAssetId]);

  /* ---------- SHAPE DATA ---------- */
  const structuredData = useMemo(() => {
    const groups = {};

    newsData.forEach((item) => {
      if (!item?.group_name) return;

      if (!groups[item.group_name]) {
        groups[item.group_name] = {
          group_name: item.group_name,
          assets: {},
        };
      }

      (item.assets || []).forEach((asset) => {
        if (!groups[item.group_name].assets[asset.id]) {
          groups[item.group_name].assets[asset.id] = {
            id: asset.id,
            firstname: asset.firstname,
            lastname: asset.lastname,
            news: asset.news || [],
            title_clusters: asset.title_clusters || [],
            total_news_count: asset.total_news_count || 0,
          };
        }
      });
    });

    return Object.values(groups)
      .map((group) => ({
        ...group,
        assets: Object.values(group.assets),
      }))
      .sort((a, b) => a.group_name.localeCompare(b.group_name));
  }, [newsData]);

  const selectedGroup = useMemo(
    () =>
      structuredData.find((group) => group.group_name === selectedGroupName) ||
      null,
    [structuredData, selectedGroupName]
  );

  const selectedAsset = useMemo(
    () =>
      selectedGroup?.assets.find(
        (asset) => String(asset.id) === String(selectedAssetId)
      ) || null,
    [selectedGroup, selectedAssetId]
  );

  useEffect(() => {
    if (selectedGroupName && !selectedGroup) {
      setSelectedGroupName(null);
      setSelectedAssetId(null);
      setExpandedGroups(null);
    }
  }, [selectedGroupName, selectedGroup]);

  const activeClusters = useMemo(() => {
    if (selectedAsset) return selectedAsset.title_clusters || [];
    if (selectedGroup) {
      return selectedGroup.assets.flatMap(
        (asset) => asset.title_clusters || []
      );
    }
    return [];
  }, [selectedGroup, selectedAsset]);

  const displayedClusters = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return [...activeClusters]
      .filter((cluster) => {
        if (!term) return true;
        return String(cluster?.primary_title || "")
          .toLowerCase()
          .includes(term);
      })
      .sort((a, b) => getClusterSources(b) - getClusterSources(a));
  }, [activeClusters, searchTerm]);

  const groupArticleTotal = useMemo(
    () =>
      selectedGroup?.assets.reduce(
        (sum, asset) => sum + getAssetArticleCount(asset),
        0
      ) || 0,
    [selectedGroup]
  );

  const totalReporting = useMemo(
    () =>
      structuredData.reduce(
        (groupTotal, group) =>
          groupTotal +
          group.assets.reduce(
            (assetTotal, asset) =>
              assetTotal + getAssetArticleCount(asset),
            0
          ),
        0
      ),
    [structuredData]
  );

  const totalAssets = useMemo(
    () =>
      structuredData.reduce(
        (total, group) => total + group.assets.length,
        0
      ),
    [structuredData]
  );

  const fetchNewsWithFilters = async () => {
    if (!userToken) return;

    const payload = [
      {
        userToken: userToken,
        groupName: selectedGroup?.group_name || "",
        assetId: selectedAsset?.id || "",
        startDate: selectedDateRange?.start || "",
        endDate: selectedDateRange?.end || "",
      },
    ];

    setIsRefreshing(true);

    try {
      const res = await fetch(
        "https://api.weathervaneintel.com/api/api/fetchMyNews",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (res.status === 403) {
        navigate("/login");
        return;
      }

      if (!res.ok) {
        console.warn("API Error:", res.status, res.statusText);
        return;
      }

      const data = await res.json();
      setNewsData(Array.isArray(data) ? data : []);
      setDismissedNews(new Set());
      setExitingNews(new Set());
    } catch (err) {
      console.error("News fetch failed:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const exportNewsToMarkdown = () => {
    if (!selectedGroup || displayedClusters.length === 0) return;

    const exportDate = new Date();

    const viewName = selectedAsset
      ? `${selectedAsset.firstname || ""} ${selectedAsset.lastname || ""
        }`.trim()
      : selectedGroup.group_name;

    const dateRangeLabel =
      selectedDateRange.start || selectedDateRange.end
        ? `${selectedDateRange.start || "Beginning"} through ${selectedDateRange.end || "Present"
        }`
        : "Default reporting period";

    const visibleClusters = displayedClusters.filter((cluster, index) => {
      const clusterKey =
        cluster.primary_title || `headline-cluster-${index}`;

      return !dismissedNews.has(clusterKey);
    });

    const markdownLines = [
      "# News Intelligence Export",
      "",
      `**Monitoring group:** ${escapeMarkdown(
        selectedGroup.group_name
      )}`,
      `**Monitored asset:** ${selectedAsset
        ? escapeMarkdown(viewName)
        : "All assets in selected group"
      }`,
      `**Reporting period:** ${escapeMarkdown(dateRangeLabel)}`,
      `**Exported:** ${exportDate.toLocaleString()}`,
      `**Headline clusters:** ${visibleClusters.length}`,
      "",
      "## Executive Context",
      "",
      selectedAsset
        ? `This document contains clustered reporting associated with the monitored asset **${escapeMarkdown(
          viewName
        )}** within the **${escapeMarkdown(
          selectedGroup.group_name
        )}** monitoring group.`
        : `This document contains clustered reporting associated with the **${escapeMarkdown(
          selectedGroup.group_name
        )}** monitoring group.`,
      "",
      "The source material below is organized by headline cluster. Each cluster may contain multiple articles covering the same or a closely related event.",
      "",
      "---",
      "",
    ];

    visibleClusters.forEach((cluster, clusterIndex) => {
      const articles = Array.isArray(cluster.articles)
        ? cluster.articles
        : [];

      const sourceCount = getClusterSources(cluster);

      markdownLines.push(
        `## ${clusterIndex + 1}. ${escapeMarkdown(
          cluster.primary_title || "Untitled headline cluster"
        )}`,
        "",
        `**Source count:** ${sourceCount}`,
        ""
      );

      if (articles.length === 0) {
        markdownLines.push(
          "_No source links were available for this cluster._",
          ""
        );
      } else {
        markdownLines.push("### Sources", "");

        articles.forEach((article, articleIndex) => {
          const domain = escapeMarkdown(
            article.domain || `Source ${articleIndex + 1}`
          );

          const link = String(article.link || "").trim();

          if (link) {
            markdownLines.push(
              `${articleIndex + 1}. [${domain}](${link})`
            );
          } else {
            markdownLines.push(
              `${articleIndex + 1}. ${domain}`
            );
          }
        });

        markdownLines.push("");
      }

      markdownLines.push("---", "");
    });

    markdownLines.push(
      "## Notes for downstream analysis",
      "",
      "- Treat headline clusters as collections of related reporting, not necessarily independent events.",
      "- Review original source links before making factual or operational judgments.",
      "- Source volume reflects collection results and should not be interpreted as confirmation of accuracy.",
      ""
    );

    const markdownContent = markdownLines.join("\n");
    const blob = new Blob([markdownContent], {
      type: "text/markdown;charset=utf-8",
    });

    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = downloadUrl;
    link.download = `${createMarkdownFileName(
      `${viewName}-news-intelligence`
    )}-${exportDate.toISOString().slice(0, 10)}.md`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(downloadUrl);
  };

  const handleDismiss = (clusterKey) => {
    setExitingNews((previous) => new Set(previous).add(clusterKey));

    window.setTimeout(() => {
      setDismissedNews((previous) => new Set(previous).add(clusterKey));
      setExitingNews((previous) => {
        const next = new Set(previous);
        next.delete(clusterKey);
        return next;
      });
    }, 300);
  };

  const selectGroup = (group) => {
    setSelectedGroupName(group.group_name);
    setSelectedAssetId(null);
    setExpandedGroups((current) =>
      current === group.group_name ? null : group.group_name
    );

    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  const selectAsset = (asset) => {
    setSelectedAssetId(asset.id);
    setDesktopSidebarOpen(false);
    setMobileSidebarOpen(false);
  };

  const selectGroupFromLandscape = (groupName) => {
    const group = structuredData.find(
      (item) => item.group_name === groupName
    );

    if (!group) return;

    setSelectedGroupName(group.group_name);
    setSelectedAssetId(null);
    setExpandedGroups(group.group_name);
  };

  const resetFilters = () => {
    setSelectedDateRange({ start: "", end: "" });
    setSelectedAssetId(null);
  };

  const closeMobilePanels = () => {
    setMobileSidebarOpen(false);
    setOptionsOpen(false);
  };

  if (!authChecked) {
    return <NewsLoadingState />;
  }

  if (!userToken) {
    return null;
  }

  return (
    <main className="news-page">
      <div className="news-contour-background" aria-hidden="true" />

      <div className="news-shell">
        <header className="news-page-header">
          <div className="news-header-copy">
            <span className="news-eyebrow">Reporting environment</span>
            <h1>News Intelligence</h1>
            <p>
              Review reporting activity, recurring narratives, and source
              coverage across monitored groups and assets.
            </p>
          </div>

          <div className="news-header-status">
            <div className="news-status-blip" aria-hidden="true">
              <span className="news-status-blip-ring" />
              <span className="news-status-blip-sweep" />
              <span className="news-status-blip-dot" />
            </div>

            <div className="news-header-status-copy">
              <span>Current view</span>
              <strong>
                {selectedAsset
                  ? `${selectedAsset.firstname || ""} ${selectedAsset.lastname || ""
                    }`.trim()
                  : selectedGroup?.group_name || "All monitoring groups"}
              </strong>
            </div>
          </div>
        </header>

        <section
          className="news-summary-strip"
          aria-label="Current news collection totals"
        >
          <div>
            <span>Monitoring groups</span>
            <strong>{structuredData.length}</strong>
          </div>
          <div>
            <span>Monitored assets</span>
            <strong>{totalAssets}</strong>
          </div>
          <div>
            <span>Collected reporting</span>
            <strong>{totalReporting}</strong>
          </div>
        </section>

        <div className="news-layout">
          <aside className="news-controls">
            <div className="news-control-header">
              <span>View controls</span>
              <strong>News analysis</strong>
            </div>

            <div className="news-control-buttons">
              <button
                type="button"
                className={`news-control-button ${desktopSidebarOpen || mobileSidebarOpen ? "active" : ""
                  }`}
                onClick={() => {
                  if (isMobile) {
                    setMobileSidebarOpen((current) => !current);
                  } else {
                    setDesktopSidebarOpen((current) => !current);
                  }

                  setOptionsOpen(false);
                }}
                aria-expanded={
                  isMobile ? mobileSidebarOpen : desktopSidebarOpen
                }
              >
                <span>Monitoring groups</span>
                <small>
                  {desktopSidebarOpen || mobileSidebarOpen ? "Close" : "Open"}
                </small>
              </button>

              <button
                type="button"
                className={`news-control-button ${optionsOpen ? "active" : ""
                  }`}
                onClick={() => {
                  setOptionsOpen((current) => !current);
                  setDesktopSidebarOpen(false);
                  setMobileSidebarOpen(false);
                }}
                aria-expanded={optionsOpen}
              >
                <span>Filter reporting</span>
                <small>{optionsOpen ? "Close" : "Open"}</small>
              </button>
            </div>

            {!isMobile && (
              <div className="news-control-selection">
                <span>Selected monitoring group</span>
                <strong>
                  {selectedGroup?.group_name || "None selected"}
                </strong>
                {selectedAsset ? (
                  <small>
                    {selectedAsset.firstname} {selectedAsset.lastname}
                  </small>
                ) : null}
              </div>
            )}

            {((isMobile &&
              (mobileSidebarOpen || optionsOpen)) ||
              (!isMobile &&
                (desktopSidebarOpen || optionsOpen))) && (
                <div className="news-filter-panel">
                  {optionsOpen ? (
                    <div className="news-options-box">
                      <div className="news-filter-panel-header">
                        <span>Reporting period</span>
                        <h2>Filter collected news</h2>
                        <p>
                          Limit the current view by date and monitored asset.
                        </p>
                      </div>

                      <div className="news-filter-field">
                        <label htmlFor="newsStartDate">Start date</label>
                        <input
                          id="newsStartDate"
                          type="date"
                          value={selectedDateRange.start}
                          onChange={(event) =>
                            setSelectedDateRange((previous) => ({
                              ...previous,
                              start: event.target.value,
                            }))
                          }
                        />
                      </div>

                      <div className="news-filter-field">
                        <label htmlFor="newsEndDate">End date</label>
                        <input
                          id="newsEndDate"
                          type="date"
                          value={selectedDateRange.end}
                          onChange={(event) =>
                            setSelectedDateRange((previous) => ({
                              ...previous,
                              end: event.target.value,
                            }))
                          }
                        />
                      </div>

                      {selectedGroup ? (
                        <div className="news-filter-field">
                          <label htmlFor="newsAssetFilter">
                            Monitored asset
                          </label>
                          <select
                            id="newsAssetFilter"
                            value={selectedAsset?.id || ""}
                            onChange={(event) => {
                              setSelectedAssetId(
                                event.target.value || null
                              );
                            }}
                          >
                            <option value="">All assets in group</option>
                            {selectedGroup.assets.map((asset) => (
                              <option key={asset.id} value={asset.id}>
                                {asset.firstname} {asset.lastname}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : null}

                      <div className="news-filter-actions">
                        <button
                          type="button"
                          className="news-reset-button"
                          onClick={resetFilters}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="news-apply-button"
                          onClick={async () => {
                            await fetchNewsWithFilters();
                            setOptionsOpen(false);
                          }}
                          disabled={isRefreshing}
                        >
                          {isRefreshing ? "Updating…" : "Apply view"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="news-group-list">
                      <div className="news-filter-panel-header">
                        <span>Monitoring environment</span>
                        <h2>Select a group</h2>
                        <p>
                          Choose a group, then narrow the view to a monitored
                          asset when needed.
                        </p>
                      </div>

                      {structuredData.map((group) => {
                        const groupTotalArticles = group.assets.reduce(
                          (sum, asset) =>
                            sum + getAssetArticleCount(asset),
                          0
                        );

                        const isExpanded =
                          expandedGroups === group.group_name;

                        return (
                          <div
                            key={group.group_name}
                            className="news-group-block"
                          >
                            <button
                              type="button"
                              className={`news-group-header ${selectedGroup?.group_name ===
                                  group.group_name
                                  ? "selected"
                                  : ""
                                }`}
                              onClick={() => selectGroup(group)}
                              aria-expanded={isExpanded}
                            >
                              <span className="news-group-identity">
                                <strong>{group.group_name}</strong>
                                <small>
                                  {group.assets.length} monitored{" "}
                                  {group.assets.length === 1
                                    ? "asset"
                                    : "assets"}
                                </small>
                              </span>

                              <span className="news-group-count">
                                <strong>{groupTotalArticles}</strong>
                                <small>articles</small>
                              </span>
                            </button>

                            {isExpanded ? (
                              <div className="news-asset-list">
                                {group.assets.map((asset) => (
                                  <button
                                    type="button"
                                    key={asset.id}
                                    className={`news-asset-card ${selectedAsset?.id === asset.id
                                        ? "active"
                                        : ""
                                      }`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      selectAsset(asset);
                                    }}
                                  >
                                    <span className="news-asset-identity">
                                      <strong>
                                        {asset.firstname} {asset.lastname}
                                      </strong>
                                      <small>Monitored asset</small>
                                    </span>

                                    <span className="news-asset-count">
                                      <strong>
                                        {getAssetArticleCount(asset)}
                                      </strong>
                                      <small>articles</small>
                                    </span>
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
          </aside>

          {isMobile &&
            (mobileSidebarOpen || optionsOpen) ? (
            <button
              type="button"
              className="news-mobile-overlay"
              aria-label="Close news controls"
              onClick={closeMobilePanels}
            />
          ) : null}

          <section className="news-content">
            {newsLoading ? (
              <div className="news-content-loading" role="status" aria-live="polite">
                <div className="news-content-loading-graphic" aria-hidden="true">
                  <span className="news-content-loading-ring news-content-loading-ring--outer" />
                  <span className="news-content-loading-ring news-content-loading-ring--inner" />
                  <span className="news-content-loading-sweep" />
                  <span className="news-content-loading-dot" />
                </div>

                <div>
                  <span className="news-section-kicker">Updating reporting view</span>
                  <h2>Loading news intelligence</h2>
                  <p>
                    Retrieving current reporting, headline clusters, and source
                    coverage for your monitored environment.
                  </p>
                </div>
              </div>
            ) : newsData.length === 0 ? (
              <EmptyState
                title="No content found"
                message="No news reporting matched the current parameters. Adjust the filters or confirm that monitored assets are configured."
              />
            ) : !selectedGroup ? (
              <section className="news-overview">
                <header className="news-section-header">
                  <div>
                    <span className="news-section-kicker">
                      Monitoring landscape
                    </span>
                    <h2>Select a group to begin analysis</h2>
                    <p>
                      This overview shows relative reporting volume across
                      monitored groups and assets. Select a group in the
                      visualization or use the monitoring controls.
                    </p>
                  </div>

                  <div className="news-section-metric">
                    <span>Current reporting</span>
                    <strong>{totalReporting}</strong>
                  </div>
                </header>

                <NewsRadarDashboard
                  groups={structuredData}
                  onSelectGroup={selectGroupFromLandscape}
                />
              </section>
            ) : (
              <>
                <header className="news-section-header">
                  <div>
                    <span className="news-section-kicker">
                      {selectedAsset
                        ? "Monitored asset reporting"
                        : "Group reporting overview"}
                    </span>
                    <h2>
                      {selectedAsset
                        ? `${selectedAsset.firstname || ""} ${selectedAsset.lastname || ""
                          }`.trim()
                        : selectedGroup.group_name}
                    </h2>
                    <p>
                      {selectedAsset
                        ? "Review clustered reporting and source coverage associated with this monitored asset."
                        : "Review clustered headlines and source coverage associated with this monitoring group."}
                    </p>
                  </div>

                  <div className="news-section-metrics">
                    {!selectedAsset ? (
                      <div>
                        <span>Collected reporting</span>
                        <strong>{groupArticleTotal}</strong>
                      </div>
                    ) : null}
                    <div>
                      <span>Headline clusters</span>
                      <strong>{activeClusters.length}</strong>
                    </div>
                  </div>
                </header>

                <div className="news-toolbar">
                  <div className="news-search">
                    <label htmlFor="newsHeadlineSearch">
                      Filter headline clusters
                    </label>
                    <div className="news-search-field">
                      <span aria-hidden="true">⌕</span>
                      <input
                        id="newsHeadlineSearch"
                        type="search"
                        placeholder="Search collected headlines"
                        value={searchTerm}
                        onChange={(event) =>
                          setSearchTerm(event.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="news-toolbar-actions">
                    {searchTerm ? (
                      <button
                        type="button"
                        className="news-clear-search"
                        onClick={() => setSearchTerm("")}
                      >
                        Clear search
                      </button>
                    ) : null}

                    <button
                      type="button"
                      className="news-export-button"
                      onClick={exportNewsToMarkdown}
                      disabled={displayedClusters.length === 0}
                      title="Export visible headline clusters as Markdown"
                    >
                      <span aria-hidden="true">↓</span>
                      Export Markdown
                    </button>
                  </div>
                </div>

                {displayedClusters.length === 0 ? (
                  <EmptyState
                    title="No headline clusters match"
                    message="Clear the search term or select a different monitored group or asset."
                  />
                ) : (
                  <div className="news-card-grid">
                    {displayedClusters.map((cluster, index) => {
                      const clusterKey =
                        cluster.primary_title ||
                        `headline-cluster-${index}`;

                      if (dismissedNews.has(clusterKey)) return null;

                      const sourceCount = getClusterSources(cluster);
                      const articles = Array.isArray(cluster.articles)
                        ? cluster.articles
                        : [];

                      return (
                        <article
                          key={clusterKey}
                          className={`news-card ${exitingNews.has(clusterKey)
                              ? "exiting"
                              : ""
                            }`}
                        >
                          <button
                            type="button"
                            className="news-dismiss-button"
                            onClick={() => handleDismiss(clusterKey)}
                            aria-label="Dismiss headline cluster"
                            title="Dismiss this headline cluster"
                          >
                            <span aria-hidden="true">×</span>
                          </button>

                          <header className="news-card-header">
                            <span>Headline cluster</span>
                            <h3>
                              {highlightText(
                                cluster.primary_title,
                                searchTerm
                              )}
                            </h3>
                          </header>

                          <div className="news-source-summary">
                            <span>Source coverage</span>
                            <strong>
                              {sourceCount}{" "}
                              {sourceCount === 1
                                ? "source"
                                : "sources"}
                            </strong>
                          </div>

                          <div className="news-source-list">
                            {articles.map((article, articleIndex) => (
                              <button
                                type="button"
                                key={`${article.link}-${articleIndex}`}
                                className="news-source-button"
                                onClick={() =>
                                  window.open(
                                    article.link,
                                    "_blank",
                                    "noopener,noreferrer"
                                  )
                                }
                              >
                                <span className="news-source-content">
                                  <strong>{article.domain}</strong>
                                  <small>{article.link}</small>
                                </span>
                                <span
                                  className="news-source-open"
                                  aria-hidden="true"
                                >
                                  ↗
                                </span>
                              </button>
                            ))}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};
